import React, { useState, useEffect } from 'react';
import API_CONFIG from '../../config/api';

const MarketData = () => {
  const [apiData, setApiData] = useState(null);
  const [apiLoading, setApiLoading] = useState(true);

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setApiLoading(true);
        
        // Fetch data from your API endpoint
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/homepage-external-price/`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Transform API data to our format dynamically from pairs_data
        const transformedData = Object.entries(data.pairs_data || {}).map(([pairKey, pairData]) => {
          const baseSymbol = pairData.pair_data?.base?.code || pairKey.split('-')[0];
          const quoteSymbol = pairData.pair_data?.quote?.code || pairKey.split('-')[1];
          
          // Create dynamic full name from base symbol - use the symbol itself as fallback
          const fullName = baseSymbol === 'BTC' ? 'Bitcoin' :
                          baseSymbol === 'ETH' ? 'Ethereum' :
                          baseSymbol === 'BNB' ? 'BNB' :
                          baseSymbol === 'USDC' ? 'USD Coin' :
                          baseSymbol === 'USDT' ? 'Tether' :
                          baseSymbol === 'TRX' ? 'TRON' :
                          baseSymbol === 'BTC1' ? 'Bitcoin1' :
                          baseSymbol; // Use symbol as full name if not in common list
          
          return {
            id: pairKey.toLowerCase().replace('-', '_'),
            symbol: baseSymbol,
            fullName: fullName,
            price: pairData.price || 0,
            price_24h_change: pairData.price_24h_value || 0,
            volume_24h: pairData.volume || 0,
            market_cap: null // API doesn't provide market cap
          };
        });

        setApiData(transformedData);
      } catch (error) {
        console.error('Error fetching market data:', error);
        // Set empty data if API fails
        setApiData([]);
      } finally {
        setApiLoading(false);
      }
    };

    fetchMarketData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(fetchMarketData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getIconColor = (symbol) => {
    const colors = {
      'BTC': 'bg-orange-500',
      'ETH': 'bg-purple-500',
      'BNB': 'bg-[#0E1696]',
      'USDC': 'bg-blue-500',
      'USDT': 'bg-green-500',
      'TRX': 'bg-red-500',
      'BTC1': 'bg-orange-600',
      'XRP': 'bg-[#0E1696]',
      'SOL': 'bg-green-600',
      'DOGE': 'bg-[#0E1696]',
      'GT': 'bg-blue-500'
    };
    return colors[symbol] || 'bg-gray-500';
  };

  const getLogoPath = (symbol) => {
    const logoMap = {
      'BTC': '/btc-logo.svg',
      'BTC1': '/btc1-logo.svg',
      'ETH': '/eth-logo.svg',
      'TRX': '/trx-logo.svg',
      'FTT': '/ftt-logo.svg'
    };
    return logoMap[symbol] || null;
  };

  // Deduplicate pairs by base currency (symbol) - keep only first occurrence
  const deduplicatedData = apiData ? apiData.filter((coin, index, array) => 
    array.findIndex(item => item.symbol === coin.symbol) === index
  ) : [];

  // Get display data - first 6 pairs for the 2x3 grid
  const displayData = deduplicatedData ? deduplicatedData.slice(0, 6) : [];

  const handleViewAll = () => {
    window.location.href = '/market';
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gray-900 backdrop-blur-sm relative overflow-hidden" style={{
      background: 'radial-gradient(69.62% 130.18% at 100% 97.01%, #384991 0%, #161837 72.6%)'
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
          {/* Left Column - Market Insights */}
          <div className="space-y-6 sm:space-y-8 flex flex-col justify-center">
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2">
                Market <span className="text-white">Insights</span>
              </h2>
              <p className="text-white text-lg sm:text-xl mb-6 sm:mb-0">Live</p>
            </div>

            <ul className="space-y-4 sm:space-y-5 mb-6 sm:mb-8">
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 bg-[#0E1696] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-white text-sm sm:text-base md:text-lg">
                  Top gainers & losers updated in real-time
                </span>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 bg-[#0E1696] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-white text-sm sm:text-base md:text-lg">
                  Rapid market movements & hot trading pairs
                </span>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 bg-[#0E1696] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-white text-sm sm:text-base md:text-lg">
                  24h volume highlights across BTC, ETH, and altcoins
                </span>
              </li>
              <li className="flex items-start gap-3 sm:gap-4">
                <div className="w-2 h-2 bg-[#0E1696] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-white text-sm sm:text-base md:text-lg">
                  Institutional liquidity indicators
                </span>
              </li>
            </ul>

            <button
              onClick={handleViewAll}
              className="bg-[#0E1696] hover:bg-[#0E1696] !text-white font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-full transition-colors text-base sm:text-lg md:w-[300px] sm:w-auto"
            >
              View Full Market
            </button>
          </div>

          {/* Right Column - Cryptocurrency Cards Grid */}
          <div className="space-y-4 sm:space-y-6">
            {apiLoading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="loading-spinner w-8 h-8 sm:w-12 sm:h-12 border-4 border-[#2A356D] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : displayData && displayData.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {displayData.map((coin) => (
                  <div
                    key={coin.id}
                    className="bg-[#1a1b3a] border border-[#2A356D] rounded-lg p-4 sm:p-5 hover:border-[#2A356D] transition-colors cursor-pointer"
                  >
                    <div className="flex flex-col items-start space-y-3">
                      {/* Logo and Symbol */}
                      <div className="flex items-center gap-2 sm:gap-3">
                        {getLogoPath(coin.symbol) ? (
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center overflow-hidden">
                            <img 
                              src={getLogoPath(coin.symbol)} 
                              alt={coin.symbol}
                              className="w-full h-full object-contain" 
                            />
                          </div>
                        ) : (
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 ${getIconColor(coin.symbol)} rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold`}>
                            {coin.symbol.slice(0, 2)}
                          </div>
                        )}
                        <div>
                          <div className="text-white font-semibold text-sm sm:text-base">{coin.symbol}</div>
                          <div className="text-white text-xs sm:text-sm">{coin.fullName}</div>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="w-full">
                        <div className="text-white font-semibold text-base sm:text-lg mb-1">
                          ${coin.price ? Number(coin.price).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }) : '0.00'}
                        </div>
                        {/* 24h Change */}
                        {coin.price_24h_change !== undefined && coin.price_24h_change !== null && (
                          <div className={`text-sm sm:text-base font-medium ${
                            coin.price_24h_change >= 0 ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {coin.price_24h_change >= 0 ? '+' : ''}
                            {coin.price_24h_change ? coin.price_24h_change.toFixed(2) : '0.00'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="text-white text-sm sm:text-base">No market data available</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketData;